export type CreditSaleVoidInput = {
  deviceSecret: string;
  sessionToken: string;
  orderId: string;
  reason: string;
  requestKey: string;
  clientContext?: Record<string, unknown>;
};

export type CreditSaleVoidSyncResult = {
  ok: boolean;
  contract: "VOID_POS_CREDIT_SALE_V1";
  result_status: "VOIDED" | "IDEMPOTENT_REPLAY" | "ALREADY_VOIDED";
  idempotent_replay?: boolean;
  atomic?: boolean;
  event_id: string;
  order_id: string;
  order_no: string;
  order_status: "VOIDED";
  receivable_id: string;
  receivable_no: string;
  receivable_status: "VOIDED";
  currency_code: string;
  amount: number;
  stock_reversal_count: number;
  stock_reversals: unknown[];
  reason: string;
  request_key: string;
  voided_at: string;
};

export type CreditSaleVoidGatewayResult = {
  ok: boolean;
  data?: CreditSaleVoidSyncResult;
  error?: string;
};

export class CreditSaleVoidGatewayClient {
  private readonly baseUrl: string;
  private readonly timeoutMs: number;

  constructor(options: { baseUrl: string; timeoutMs?: number }) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.timeoutMs = options.timeoutMs ?? 15_000;
  }

  async voidSale(input: CreditSaleVoidInput): Promise<CreditSaleVoidGatewayResult> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), this.timeoutMs);

    try {
      const response = await fetch(`${this.baseUrl}/api/pos/runtime/credit-sale/void`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          device_secret: input.deviceSecret,
          session_token: input.sessionToken,
          order_id: input.orderId,
          reason: input.reason,
          request_key: input.requestKey,
          client_context: input.clientContext ?? {},
        }),
        signal: controller.signal,
        credentials: "omit",
      });

      const payload = (await response.json().catch(() => null)) as CreditSaleVoidGatewayResult | null;
      if (!response.ok || !payload?.ok || !payload.data) {
        return { ok: false, error: payload?.error ?? `GATEWAY_HTTP_${response.status}` };
      }
      return payload;
    } catch (error) {
      return {
        ok: false,
        error:
          error instanceof DOMException && error.name === "AbortError"
            ? "GATEWAY_TIMEOUT"
            : "GATEWAY_UNREACHABLE",
      };
    } finally {
      window.clearTimeout(timeout);
    }
  }
}
